'use client'

import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  use,
  useContext,
  useState,
} from 'react'
import useSWR from 'swr'

import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { CoOwner } from '@/services/cms'
import { getPublicFollowers, UserFollowResult } from '@/services/community'

type WorldCommunityContextType = {
  coOwners?: CoOwner[]
  followersData?: UserFollowResult
  setFollowersData: Dispatch<SetStateAction<UserFollowResult | undefined>>
}

const WorldCommunityContext = createContext<WorldCommunityContextType | null>(null)

type WorldCommunityProviderProps = PropsWithChildren<{
  coOwnersPromise: Promise<CoOwner[] | undefined> | null
  followersPromise: Promise<UserFollowResult | undefined> | null
}>

export function WorldCommunityProvider({
  children,
  coOwnersPromise,
  followersPromise,
}: WorldCommunityProviderProps) {
  const coOwners = coOwnersPromise ? use(coOwnersPromise) : undefined
  const followers = followersPromise ? use(followersPromise) : undefined

  const { roomData } = useCheckEnterRoomContext()
  const [followersData, setFollowersData] = useState(followers)

  useSWR(
    followers ? 'getFollowersData' : null,
    () => getPublicFollowers(roomData!.owner.htc_account_id).then((res) => res?.results),
    {
      // 🥷🏼 This is the initial data, wont be called at first render
      fallbackData: followers,
      // 🥷🏼 We do this for revalidate when page refocus
      onSuccess: (data) => {
        setFollowersData(data)
      },
    },
  )

  return (
    <WorldCommunityContext.Provider value={{ coOwners, followersData, setFollowersData }}>
      {children}
    </WorldCommunityContext.Provider>
  )
}

export const useWorldCommunityContext = () => {
  const value = useContext(WorldCommunityContext)

  if (value == null) {
    throw new Error('useWorldCommunityContext cannot be used outside of WorldCommunityProvider')
  }

  return value
}
